/* eslint no-await-in-loop: "off" */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsDatabaseFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@nextui-org/react";
import { useParams } from "react-router-dom";
import { MdOutlineStarBorder } from "react-icons/md";

import RepresentativeModale from "./RepresentativeModale";
import PaginationTable from "../../components/PaginationTable";
import Modal from "../../components/Modal";

import { representatives_table_columns } from "../../constants/tableHeaders";
import { getGradeBackgroundColor } from "../../utils/colorUtils";
import { useAppContext } from "../../contexts/AppContext";
import { filterSortAndPaginateOperators } from "../../utils/searchOperators";

import { getOperators, getOperatorsWithoutStore } from "../../redux/actions";
import { ArrowTopRightIcon } from "../../assets/icons/audio-icons";
import { getAlgoliaInfos } from "../../redux/actions/algoliaActions";
import ACircleUserDisplay from "../../components/Kit/ACircleUserDisplay";

const Representatives = () => {
  const { t } = useTranslation();
  let { representativeId } = useParams();

  const {
    searchText,
    callsDateRange,
    teamGrades,
    individualGrades,
    setOperatorsWithMetrics,
    representativesDepartment,
  } = useAppContext();
  const [filteredOperators, setFilteredOperators] = useState([]);
  const [filteredOperatorsCount, setFilteredOperatorsCount] = useState(0);
  const [openRepresentativeProfile, setOpenRepresentativeProfile] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hasOpenedDeepLink, setHasOpenedDeepLink] = useState(false);
  const { user } = useSelector((state) => state.user);

  const [sortDescriptor, setSortDescriptor] = useState({
    sortBy: "operator_name",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pageOperators, setPageOperators] = useState([]);

  useEffect(() => {
    setOperatorsWithMetrics([]);
  }, []);

  const fetchOperatorList = async () => {
    const operatorsFromApi = await getOperatorsWithoutStore({
      start_date: callsDateRange.startDate,
      end_date: callsDateRange.endDate,
    });

    if (!operatorsFromApi) {
      return;
    }

    const formattedOperators = operatorsFromApi.map((operator) => {
      const nbTotalCalls = operator?.nb_calls || 0;
      const nbIncomingCalls = operator?.nb_incoming_calls || 0;
      const nbOutgoingCalls = operator?.nb_outgoing_calls || 0;
      const nbFrustratedCalls = operator?.nb_frustrated_calls || 0;

      return {
        ...operator,
        nb_incoming:
          nbIncomingCalls === 0 ? 0 : Math.round((nbIncomingCalls * 100) / nbTotalCalls, 2),
        nb_outgoing:
          nbOutgoingCalls === 0 ? 0 : Math.round((nbOutgoingCalls * 100) / nbTotalCalls, 2),
        nb_frustrated:
          nbFrustratedCalls === 0 ? 0 : Math.round((nbFrustratedCalls * 100) / nbTotalCalls, 2),
      };
    });

    const formattedOperatorMetrics = operatorsFromApi.map((operator) => {
      const nbTotalCalls = operator?.nb_calls || 0;
      const nbIncomingCalls = operator?.nb_incoming_calls || 0;
      const nbOutgoingCalls = operator?.nb_outgoing_calls || 0;
      const nbFrustratedCalls = operator?.nb_frustrated_calls || 0;

      return {
        ...operator,
        "incoming calls (%)":
          nbIncomingCalls === 0 ? 0 : Math.round((nbIncomingCalls * 100) / nbTotalCalls, 2),
        "outgoing calls (%)":
          nbOutgoingCalls === 0 ? 0 : Math.round((nbOutgoingCalls * 100) / nbTotalCalls, 2),
        "frustrated calls (%)":
          nbFrustratedCalls === 0 ? 0 : Math.round((nbFrustratedCalls * 100) / nbTotalCalls, 2),
      };
    });

    setPageOperators(formattedOperators);
    setOperatorsWithMetrics(formattedOperatorMetrics);
    setIsPageLoading(false);
  };

  useEffect(() => {
    setIsPageLoading(true);
    fetchOperatorList();
  }, [user, callsDateRange]);

  useEffect(() => {
    if (hasOpenedDeepLink) {
      return;
    }
    if (!representativeId) {
      return;
    }

    setSelectedItem(representativeId);
    setOpenRepresentativeProfile(true);
    setHasOpenedDeepLink(true);
  }, [representativeId]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      getOperators({
        start_date: callsDateRange.startDate,
        end_date: callsDateRange.endDate,
      })
    );
  }, [dispatch, user, callsDateRange]);

  useEffect(() => {
    dispatch(getAlgoliaInfos());
  }, [dispatch]);

  useEffect(() => {
    const { operators: filteredOperators, nbFiltered: nbFilteredOperators } =
      filterSortAndPaginateOperators(
        pageOperators,
        searchText,
        teamGrades,
        individualGrades,
        sortDescriptor,
        rowsPerPage,
        page - 1,
        representativesDepartment
      );

    setFilteredOperators(filteredOperators);
    setFilteredOperatorsCount(nbFilteredOperators);
  }, [
    pageOperators,
    individualGrades,
    teamGrades,
    searchText,
    sortDescriptor,
    rowsPerPage,
    page,
    representativesDepartment,
  ]);

  // Back to the first page when the department filter narrows the list.
  useEffect(() => {
    setPage(1);
  }, [representativesDepartment]);

  const renderTableEmptyState = () => {
    return (
      <div className="w-full h-full flex-1 flex flex-col items-center justify-center bg-white rounded-3xl py-12">
        <BsDatabaseFill className="text-gray-300 w-12 h-12" />
        <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
      </div>
    );
  };

  const renderLoading = () => (
    <CircularProgress
      size="sm"
      aria-label="Loading..."
      color="primary"
    />
  );

  const renderCell = (item, columnKey) => {
    const cellValue = item[columnKey];
    const individualGradeNormalized =
      Math.round((item.average_grade + Number.EPSILON) * 10) / 10 || 0;
    const teamGradeNormalized = Math.round((item.team_grade + Number.EPSILON) * 10) / 10 || 0;

    const individualGradeNormalizedBgColor = getGradeBackgroundColor(individualGradeNormalized);
    const teamGradeNormalizedBgColor = getGradeBackgroundColor(teamGradeNormalized);

    switch (columnKey) {
      case "operator_name":
        return (
          <ACircleUserDisplay
            firstname={item.first_name}
            lastname={item.last_name}
            email={item.email}
          />
        );
      case "grade":
        return (
          <div className="w-full flex justify-start items-center">
            <div
              className={`flex justify-start items-center font-semibold text-center px-[8px] py-[4px] rounded-md text-xs ${individualGradeNormalizedBgColor}`}
            >
              <MdOutlineStarBorder size={20} />
              <span className="mx-[4px]">{individualGradeNormalized}</span>
            </div>
          </div>
        );
      case "team_grade":
        return (
          <div className="w-full flex justify-start items-center">
            <div
              className={`flex justify-start items-center font-semibold text-center px-[8px] py-[4px] rounded-md text-xs ${teamGradeNormalizedBgColor}`}
            >
              <MdOutlineStarBorder size={20} />
              <span className="mx-[4px]">{teamGradeNormalized}</span>
            </div>
          </div>
        );
      case "nb_calls":
        return item?.nb_calls || item?.nb_calls === 0 ? (
          <div className="text-sm text-[#737373] relative flex justify-end items-center">
            {item?.nb_calls}
          </div>
        ) : (
          renderLoading()
        );
      case "nb_outgoing":
        return item?.nb_outgoing || item?.nb_outgoing === 0 ? (
          <div className="text-sm text-[#737373] relative flex justify-end items-center">{`${item?.nb_outgoing}%`}</div>
        ) : (
          renderLoading()
        );
      case "nb_incoming":
        return item?.nb_incoming || item?.nb_incoming === 0 ? (
          <div className="text-sm text-[#737373] relative flex justify-end items-center">{`${item?.nb_incoming}%`}</div>
        ) : (
          renderLoading()
        );
      case "nb_frustrated":
        return item?.nb_frustrated || item?.nb_frustrated === 0 ? (
          <div className="text-sm text-[#737373] relative flex justify-end items-center">{`${item?.nb_frustrated}%`}</div>
        ) : (
          renderLoading()
        );
      case "nb_frustrated_calls":
        return item?.nb_frustrated || item?.nb_frustrated === 0 ? (
          <div className="text-sm text-[#737373] relative flex justify-end items-center">
            {item?.nb_frustrated_calls}
          </div>
        ) : (
          renderLoading()
        );
      case "actions":
        return (
          <div className="w-full h-full flex items-center justify-end">
            <div className="flex items-center justify-center bg-[#4318FF] rounded-md p-[10px]">
              <ArrowTopRightIcon
                size={8}
                className=" text-primary"
              />
            </div>
          </div>
        );
      default:
        return (
          <span className="text-sm text-[#404040]/[0.6] w-full float-left whitespace-normal">
            {cellValue}
          </span>
        );
    }
  };

  const handleOnRowAction = (operator) => () => {
    if (operator) {
      setSelectedItem(operator.id);
      setOpenRepresentativeProfile(true);
    }
  };

  return (
    <div className="p-6 pb-32">
      <PaginationTable
        items={filteredOperators}
        columns={representatives_table_columns}
        emptyState={renderTableEmptyState()}
        renderCell={renderCell}
        onRowAction={handleOnRowAction}
        sortDescriptor={sortDescriptor}
        setSortDescriptor={setSortDescriptor}
        isLoading={isPageLoading}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        displaySelectAll={false}
        itemNaming="representatives"
        overrideTotalItems={filteredOperatorsCount}
      />
      {openRepresentativeProfile && (
        <Modal
          isOpen={openRepresentativeProfile}
          showFooter={false}
          size="4xl"
          handleOnClose={() => {
            setOpenRepresentativeProfile(false);
          }}
        >
          <RepresentativeModale
            operatorId={selectedItem}
            handleOnClose={() => setOpenRepresentativeProfile(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default Representatives;
